import React, { useState, useEffect } from 'react';
import Icon from 'components/commons/icon/Icon';

export type PageParamType = {
  startPage: number;
  selectPage: number;
  totalPageCount: number;
};

type PaginationProps = {
  startPage: number | undefined;
  selectPage: number | undefined;
  totalPageCount: number | undefined;
  onChangePage: (newPageParam: PageParamType) => void;
};

// ページボタンのテキスト
function getPageButtonsText(pageParam: PageParamType): string[] {
  const { startPage, totalPageCount } = pageParam;
  const pageBtnCount = calcDisplayPageButtonNum();

  if (pageBtnCount <= 2 || startPage === 0 || totalPageCount === 0) {
    return [];
  }

  let len = totalPageCount - startPage + 1;
  if (len > pageBtnCount - 2) {
    len = pageBtnCount - 2;
  }

  const arr: string[] = new Array(len);
  for (let n = 0; n < len; n++) {
    arr[n] = (startPage + n).toString();
  }
  arr.unshift('<');
  arr.push('>');

  return arr;
}

// 画面幅に合わせたページボタンの表示数
function calcDisplayPageButtonNum(): number {
  const maxCnt = 10;
  const num = Math.floor(window.parent.screen.width / 40) - 2;
  return num > maxCnt ? maxCnt : num;
}

// ページ管理の初期値
function getInitPageParamType(
  startPage: number,
  selectPage: number,
  totalPageCount: number
): PageParamType {
  let initStartPage = 0;
  let initSelectPage = 0;
  let initTotalPageCount = 0;

  if (
    startPage <= selectPage &&
    selectPage <= totalPageCount &&
    selectPage < startPage + totalPageCount
  ) {
    initStartPage = startPage;
    initSelectPage = selectPage;
    initTotalPageCount = totalPageCount;
  }

  return {
    startPage: initStartPage,
    selectPage: initSelectPage,
    totalPageCount: initTotalPageCount,
  };
}

// ページネーション
const Pagination: React.FC<PaginationProps> = props => {
  const { startPage, selectPage, totalPageCount, onChangePage } = props;
  const [pageParam, setPageParam] = useState<PageParamType>(() =>
    getInitPageParamType(startPage ?? 0, selectPage ?? 0, totalPageCount ?? 0)
  );

  // console.log("Pagination", startPage, selectPage, totalPageCount);

  // ページボタンのテキスト
  const pageButtonsText = getPageButtonsText(pageParam);

  // ページボタンビュー
  const paginationView = pageButtonsText.map((item, index) => {
    const btnClass =
      (item === pageParam?.selectPage.toString()
        ? 'bg-blue-700 text-white'
        : 'bg-white cursor-pointer') +
      ' text-center items-center rounded w-10 h-10 p-2 m-1';

    const onPageChange = () => {
      // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
      document.getElementById('id_notificationItemsView')!.scrollTop = 0;
      const { startPage, selectPage, totalPageCount } = pageParam;

      if (!startPage || !selectPage || !totalPageCount) return;

      let stPage = startPage;
      let newPage = selectPage;
      if (item === '<') {
        if (selectPage > 1) {
          newPage = selectPage - 1;
          if (newPage === startPage - 1) {
            stPage--;
          }
        }
      } else if (item === '>') {
        if (selectPage < totalPageCount) {
          newPage = selectPage + 1;
          const displayNum = calcDisplayPageButtonNum();
          if (newPage === startPage + displayNum - 2) {
            stPage++;
          }
        }
      } else {
        newPage = Number(item);
      }

      if (newPage !== selectPage) {
        const newPageParam = {
          ...pageParam,
          startPage: stPage,
          selectPage: newPage,
        };

        // setPageParam(newPageParam);
        onChangePage(newPageParam);
      }
    };

    return (
      <div className={btnClass} key={index} onClick={onPageChange}>
        {item === '<' ? (
          <Icon name="chevron-left" />
        ) : item === '>' ? (
          <Icon name="chevron-right" />
        ) : (
          item
        )}
      </div>
    );
  });

  useEffect(() => {
    // console.log("Pagination useEffect", startPage, selectPage, totalPageCount);

    setPageParam(currentPageParam => {
      if (
        currentPageParam.startPage === startPage &&
        currentPageParam.selectPage === selectPage &&
        currentPageParam.totalPageCount === totalPageCount
      ) {
        return currentPageParam;
      }

      const newPageParam = {
        ...currentPageParam,
        startPage: startPage ?? 0,
        selectPage: selectPage ?? 0,
        totalPageCount: totalPageCount ?? 0,
      };
      return newPageParam;
    });
  }, [startPage, selectPage, totalPageCount]);

  return (
    <div
      className={`flex justify-center py-2 ${
        (totalPageCount ?? 0) > 1 ? '' : 'hidden'
      }`}>
      {paginationView}
    </div>
  );
};

export default Pagination;
